import React, {useRef, ElementType, memo, Suspense} from "react"
import {SelectChangeEvent} from "@mui/material"
import {MANIFEST} from "@manifest/header"
import {useTemplatedComponent} from "@next/templating"
import {
    COUNTRY_SELECTOR_DRAWER_BREAKPOINT,
    CHEVRON_ICON_URL,
    COUNTRY_SELECT_DROPDOWN_DATA_GA,
} from "../../config/constants"

import {StyledIcon, SelectedMenuItem, StyledOutlinedInput, SelectedFormControl, styles} from "./component"
import {Country as CountryType, CountryLanguage} from "../../models/countryselector"
import env from "../../config/env"
import connect from "./connect"
import {useMediaQuery} from "../../hooks/useMediaQuery"

const CustomStyledIcon = props => (
    <StyledIcon {...props} className={`material-icons ${props.className}`} src={props.src} />
)

const MemoizedCustomStyledIcon = memo(CustomStyledIcon)
const CHEVRON_ICON = `${env.APP_BLOB_STORAGE_PATH}${CHEVRON_ICON_URL}`

const passWithIconSrc = (chevronIcon: string) => {
    const WrappedCustomStyledIcon = props => <MemoizedCustomStyledIcon {...props} src={chevronIcon} />
    return WrappedCustomStyledIcon
}
type CountrySelectDropdownType = {
    territory: string
    textAlignment: string
    selectedCountry: CountryType | null
    selectLanguage: (language: string) => void
    selectedLanguage: string | null
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
}

export type SelectProps = {
    value?: string
    onChange: () => void
    onOpen: () => void
    onKeyPress: () => void
    MenuProps?: object
    input?: any
    IconComponent?: ElementType
    children?: any
}

let timerId: NodeJS.Timeout | number = 0

export const CountrySelectLanguageDropdown = memo(
    ({
        territory,
        textAlignment,
        selectedCountry,
        selectLanguage,
        selectedLanguage,
        templating,
    }: CountrySelectDropdownType) => {
        const countryRef = useRef<HTMLInputElement>()
        const anchorDirectionBreakpoint = useMediaQuery(`(min-width:${COUNTRY_SELECTOR_DRAWER_BREAKPOINT})`)
        const itemAligment = textAlignment === "ltr" ? "left" : "right"

        const handleChange = (event: SelectChangeEvent<unknown>): void => {
            selectLanguage(event.target.value as string)
        }

        const onOpen = () => {
            timerId = setTimeout(() => {
                if (countryRef.current) {
                    countryRef.current.focus()
                }
                clearTimeout(timerId as NodeJS.Timeout)
            }, 500)
        }
        const getSelectedLanguageValue = selectedLangValueLocal => {
            const selectedLangLocal = selectedCountry?.Languages.filter(lang => lang.Name === selectedLanguage)
            return selectedLangLocal && selectedLangValueLocal ? selectedLangLocal[0].Name : ""
        }

        const TemplatedSelect = useTemplatedComponent(templating, MANIFEST)

        return (
            <SelectedFormControl variant="outlined">
                <Suspense>
                    <TemplatedSelect
                        value={getSelectedLanguageValue(selectedLanguage)}
                        onChange={handleChange}
                        onOpen={onOpen}
                        MenuProps={{
                            keepMounted: false,
                            disablePortal: true,
                            disableScrollLock: true,
                            anchorOrigin: {
                                vertical: anchorDirectionBreakpoint ? "bottom" : "top",
                                horizontal: itemAligment,
                            },
                            transformOrigin: {
                                vertical: anchorDirectionBreakpoint ? "top" : "bottom",
                                horizontal: itemAligment,
                            },
                        }}
                        input={
                            <StyledOutlinedInput
                                data-testid="country-language-selector-select-input"
                                name="country-language-selector-select"
                                id="country-language-selector-select"
                            />
                        }
                        data-testid="country-language-selector-select-menu"
                        IconComponent={passWithIconSrc(CHEVRON_ICON)}
                    >
                        {/* eslint-disable-next-line react/display-name */}
                        {selectedCountry?.Languages.map((lan: CountryLanguage, i) => (
                            <SelectedMenuItem
                                data-testid={`country-language-selector-${lan.Name}`}
                                data-ga-v1={COUNTRY_SELECT_DROPDOWN_DATA_GA}
                                data-ga-v2={lan.Name}
                                key={lan.Name}
                                value={lan.Name}
                                sx={styles.root}
                                onClick={() => selectLanguage(lan.Name)}
                            >
                                {lan.Value}
                            </SelectedMenuItem>
                        ))}
                    </TemplatedSelect>
                </Suspense>
            </SelectedFormControl>
        )
    },
)

CountrySelectLanguageDropdown.displayName = "CountrySelectLanguageDropdown"

export default connect(CountrySelectLanguageDropdown)
